// client/src/hooks/useAgendas.js - VERSÃO BLINDADA (Silencia Erros 404)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';

const AGENDAS_QUERY_KEY = 'agendas';

// ==============================================================================
// ✅ UTILITÁRIOS
// ==============================================================================

const validateId = (id, operation = 'operação') => {
    if (!id) return null;
    const idToValidate = id?._id || id?.id || id;
    if (!idToValidate) return null;
    
    const idStr = String(idToValidate).trim();
    if (!/^[0-9a-fA-F]{24}$/.test(idStr)) {
        return null;
    }
    return idStr;
};

const extractData = (response) => {
    if (!response || !response.data) return null;
    if (response.data.success !== undefined) {
        return response.data.data || response.data;
    }
    return response.data;
};

// ==============================================================================
// ✅ FUNÇÕES DE FETCH COM TRATAMENTO SILENCIOSO DE 404
// ==============================================================================

const fetchAgendas = async (params = {}) => {
    try {
        const response = await api.get('/agendas', { 
            params,
            // Isso diz ao axios que 404 não é uma falha de requisição que deve lançar erro
            validateStatus: (status) => (status >= 200 && status < 300) || status === 404
        });
        
        if (response.status === 404) return [];
        return extractData(response);
    } catch (error) {
        throw error;
    }
};

const fetchAgendaById = async (id) => {
    const validId = validateId(id, 'busca de agenda');
    if (!validId) return null;
    
    try {
        const response = await api.get(`/agendas/${validId}`, {
            validateStatus: (status) => (status >= 200 && status < 300) || status === 404
        });
        
        if (response.status === 404) return null;
        return extractData(response);
    } catch (error) {
        throw error;
    }
};

const fetchAgendaByEntidade = async (tipoEntidade, entidadeId) => {
    const validId = validateId(entidadeId, 'busca de agenda por entidade');
    if (!validId || !tipoEntidade) return null;

    try {
        const response = await api.get(`/agendas/entidade/${tipoEntidade}/${validId}`, {
            // ✅ CORREÇÃO CRÍTICA: Diz ao Axios para NÃO lançar erro se for 404
            validateStatus: function (status) {
                return (status >= 200 && status < 300) || status === 404;
            }
        });

        // Se retornar 404, significa apenas que a Entidade (User, Unidade, Recurso) 
        // ainda não tem uma configuração salva no banco. Retornamos nulo.
        if (response.status === 404) {
            return null;
        }

        return extractData(response);
    } catch (error) {
        // Fallback de segurança se o erro vazar
        if (error.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

// ==============================================================================
// ✅ HOOKS DE CONSULTA
// ==============================================================================

export const useAgendasQuery = (params = {}, options = {}) => {
    return useQuery({
        queryKey: [AGENDAS_QUERY_KEY, 'list', params],
        queryFn: () => fetchAgendas(params),
        staleTime: 5 * 60 * 1000,
        keepPreviousData: true,
        retry: false, 
        ...options
    });
};

export const useAgendaByIdQuery = (id, options = {}) => {
    return useQuery({
        queryKey: [AGENDAS_QUERY_KEY, id],
        queryFn: () => fetchAgendaById(id),
        enabled: !!id && (options.enabled !== false),
        staleTime: 5 * 60 * 1000,
        retry: false,
        ...options
    });
};

export const useAgendaByEntidadeQuery = (tipoEntidade, entidadeId, options = {}) => {
    return useQuery({
        queryKey: [AGENDAS_QUERY_KEY, 'entidade', tipoEntidade, entidadeId],
        queryFn: () => fetchAgendaByEntidade(tipoEntidade, entidadeId),
        enabled: !!tipoEntidade && !!entidadeId && (options.enabled !== false),
        staleTime: 5 * 60 * 1000,
        retry: false, // Se der erro, não fica retentando igual louco
        ...options
    });
};

// ==============================================================================
// ✅ HOOK DE MUTAÇÕES
// ==============================================================================

export const useAgendasMutations = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateQueries = (agendaId = null, entidadeId = null) => {
        queryClient.invalidateQueries({ queryKey: [AGENDAS_QUERY_KEY] });
        if (agendaId) {
            queryClient.invalidateQueries({ queryKey: [AGENDAS_QUERY_KEY, validateId(agendaId)] });
        }
        if (entidadeId) {
            queryClient.invalidateQueries({ queryKey: [AGENDAS_QUERY_KEY, 'entidade'] });
        }
        // Quando salva agenda, pode impactar na exibição de outras telas
        queryClient.invalidateQueries({ queryKey: ['profissionais'] });
        queryClient.invalidateQueries({ queryKey: ['recursos'] });
        queryClient.invalidateQueries({ queryKey: ['unidades'] });
    };

    const createAgenda = useMutation({
        mutationFn: async (data) => {
            const response = await api.post('/agendas', data);
            return extractData(response);
        },
        onSuccess: (data, variables) => {
            invalidateQueries(data._id, variables.entidadeId);
            setAlert('Agenda configurada com sucesso!', 'success');
        },
        onError: (error) => {
            const msg = error.response?.data?.message || error.response?.data?.error || 'Erro ao criar agenda';
            setAlert(msg, 'error');
        }
    });

    const updateAgenda = useMutation({
        mutationFn: async ({ entidadeId, agendaData }) => {
            const validId = validateId(entidadeId, 'atualização de agenda');
            if (!validId) throw new Error('ID do usuário/entidade é inválido para atualizar a agenda.');
            
            const tipoEntidade = agendaData.tipoEntidade || 'User';

            const response = await api.put(`/agendas/entidade/${tipoEntidade}/${validId}`, agendaData);
            return extractData(response);
        },
        onSuccess: (data) => {
            invalidateQueries(data._id, data.entidadeId);
            setAlert('Configurações da agenda atualizadas!', 'success');
        },
        onError: (error) => {
            const msg = error.response?.data?.message || error.response?.data?.error || 'Erro ao atualizar agenda';
            setAlert(msg, 'error');
            throw error; 
        }
    });

    const updateAgendaPermissions = useMutation({
        mutationFn: async ({ agendaId, permissoesData }) => {
            const validId = validateId(agendaId, 'atualização de permissões');
            if (!validId) throw new Error('ID inválido para atualizar permissões.');
            
            const response = await api.put(`/agendas/${validId}`, { permissoes: permissoesData });
            return extractData(response);
        },
        onSuccess: (data) => {
            invalidateQueries(data?._id);
        },
        onError: (error) => {
            const msg = error.response?.data?.error || 'Erro ao atualizar permissões da agenda';
            setAlert(msg, 'danger');
        }
    });

    return {
        createAgenda,
        updateAgenda,
        updateAgendaPermissions,
        isPending: createAgenda.isPending || updateAgenda.isPending || updateAgendaPermissions.isPending,
        isCreating: createAgenda.isPending,
        isUpdating: updateAgenda.isPending
    };
};

export default function useAgendas() {
    const mutations = useAgendasMutations();
    return {
        useAgendasQuery,
        useAgendaByIdQuery,
        useAgendaByEntidadeQuery,
        ...mutations
    };
}